import { TravelDirectionEnum } from '@wilson/activities/symbols';
import { Base } from '@wilson/base';
import { Agreement } from '../agreements/agreement';
import { ServiceDeviation } from '@wilson/service-deviation/interfaces';
import { WilsonApp } from '../wilson-app';
import { GeoLocation } from '../locations/geoLocation';
import { Profession } from '../profession/profession';
import type { ResolvedService } from '../services/service';
import type { Shift } from '../shifts/shift';
import type { Job } from '../wilson-share/job';
import { ActivityCategory } from './activity-category';
import { ActivityComment } from './activity-comment';
import { ActivityReport, ResolvedActivityReport } from './activity-report';
import { JobActivityTemplate } from './activity-template';
import {
  ActiveOperationStatus,
  OperationStatus,
  SkippedOrCancelledOperationStatus,
} from './operationStatus';

export interface Activity extends Base {
  activityCategoryId: string | null;
  createdFrom: WilsonApp;
  endDatetime: string;
  endLocationId: string | null;
  name: string;
  operationalStatus: OperationStatus;
  serviceId: string | null;
  shiftId: string | null;
  jobId: string | null;
  professionId: string | null;
  qualificationProfileIds?: string[] | null;
  // TODO: replace with `QualificationProfile[]` once the model is available
  qualificationProfiles?: { id: string }[] | null;
  startDatetime: string;
  startLocationId: string | null;
  viaLocationIds: string[];
  agreementId: string | null;
  clientId?: string | null;
  priority: number | null;
  tonnage: number | null;
  numberOfWagons: number | null;
  organizationalUnitTransportGoodsId: number | null;
  travelDirection: TravelDirectionEnum | null;
  cargo: string | null;
  track: string | null;
  note: string | null;
  externalNumber: string | null;
  orderNumber: string | null;
  trainNumber?: string | null;
  wagonNumber: string | null;
  locomotiveNumber: string | null;
  vehicleEmissionClassId: number | null;
  reportedStartDatetime?: string | null;
  reportedEndDatetime?: string | null;
  reportedStartLocationId?: string | null;
  reportedEndLocationId?: string | null;

  endLocation?: GeoLocation | null;
  activityComments?: ActivityComment[] | null;
  activityReports?: ActivityReport[] | null;
  activityCategory?: ActivityCategory | null;
  serviceDeviations?: ServiceDeviation[];
  job?: Job | null;
  profession?: Profession | null;
  agreement?: Agreement;
  viaLocations?: GeoLocation[] | null;
  startLocation?: GeoLocation | null;
  shift?: Shift;
  service?: ResolvedService | null;

  validateRoute?: boolean;
}

export interface ResolvedActivity extends Activity {
  activityCategory: ActivityCategory | null;
  startLocation: GeoLocation | null;
  endLocation: GeoLocation | null;
  activityReports: ResolvedActivityReport[];
}

/**
 * A {@link ResolvedActivity} whose `operationalStatus` is guaranteed to be
 * neither {@link OperationStatus.SkippedByUser} nor {@link OperationStatus.Cancelled}.
 *
 * Use {@link isActiveResolvedActivity} as the type predicate when filtering.
 */
export type ActiveResolvedActivity = Omit<
  ResolvedActivity,
  'operationalStatus'
> & {
  operationalStatus: ActiveOperationStatus;
};

/**
 * A {@link ResolvedActivity} whose `operationalStatus` is guaranteed to be
 * either {@link OperationStatus.SkippedByUser} or {@link OperationStatus.Cancelled}.
 *
 * Use {@link isActivitySkippedOrCancelled} as the type predicate when filtering.
 */
export type SkippedOrCancelledResolvedActivity = Omit<
  ResolvedActivity,
  'operationalStatus'
> & {
  operationalStatus: SkippedOrCancelledOperationStatus;
};

export interface BulkUpdateActivitiesDto {
  id: string;
  data: Partial<Activity>;
}

export type ResolvedActivityWithRequiredActivityReportFields = Omit<
  ResolvedActivity,
  'id' | 'startLocation' | 'endLocation' | 'startLocationId' | 'endLocationId'
> & {
  id: string;
  startLocation: GeoLocation;
  endLocation: GeoLocation;
  startLocationId: string;
  endLocationId: string;
};

export function isResolvedActivityWithRequiredFields(
  activity: ResolvedActivity,
): activity is ResolvedActivityWithRequiredActivityReportFields {
  const isGeoLocation = (
    location: GeoLocation | null,
  ): location is GeoLocation => !!location && typeof location.id === 'string';

  return (
    typeof activity.id === 'string' &&
    isGeoLocation(activity.startLocation) &&
    isGeoLocation(activity.endLocation) &&
    typeof activity.startLocationId === 'string' &&
    typeof activity.endLocationId === 'string'
  );
}

export interface ActivityWithAddress extends Activity {
  startLocation: GeoLocation;
  endLocation: GeoLocation;
}

export enum ActivityFilter {
  All = 'all',
  Planned = 'planned',
  Reported = 'reported',
}

export interface SplittedActivities {
  existingActivity: ResolvedActivity;
  newActivities: (ResolvedActivity & {
    jobActivityTemplates?: JobActivityTemplate[];
  })[];
}

export enum ActivityCategoriesIdsMap {
  Disposition = '006a0f46-2c54-4aac-9234-baebaf120010',
  TrainDrive = '73b6f3fc-8d47-4cd0-8e4a-a3a3756e8ce7',
  EmptyRide = '1a6d88df-7db5-4ffc-a3d9-063518330dbe',
  GuestRide = '2c6d5343-6248-4c2a-b93a-e85ca0d6f009',
  Break = 'c70aac31-b568-4c6f-b580-e26b53770c48',
  More = 'cec090a1-e13a-47c2-bc78-76dc73be7cc5',
}

export enum ActivityAssignmentStatus {
  Assigned = 'assigned',
  Unassigned = 'unassigned',
  InShift = 'in-shift',
}

/**
 * General-purpose status check for any activity-like object.
 *
 * Kept generic because callers across the codebase pass types other than
 * {@link ResolvedActivity} (e.g. `Activity`, hand-assembled intersections).
 * Since it returns an `is` predicate, it can be passed directly to `filter`
 * on a `ResolvedActivity[]` and the result will be assignable to
 * {@link SkippedOrCancelledResolvedActivity}[] without a cast.
 * For the inverse (active) side, use {@link isActiveResolvedActivity} —
 * TypeScript does not narrow negated predicates in `filter` callbacks.
 */
export function isActivitySkippedOrCancelled<
  T extends { operationalStatus: OperationStatus },
>(
  activity: T,
): activity is T & { operationalStatus: SkippedOrCancelledOperationStatus } {
  return (
    activity.operationalStatus === OperationStatus.SkippedByUser ||
    activity.operationalStatus === OperationStatus.Cancelled
  );
}

/**
 * Type predicate — the logical complement of {@link isActivitySkippedOrCancelled}.
 *
 * Narrows a {@link ResolvedActivity} to {@link ActiveResolvedActivity},
 * guaranteeing the `operationalStatus` is neither skipped nor cancelled.
 * A dedicated function is necessary because TypeScript does not narrow
 * negated predicates (`!isActivitySkippedOrCancelled`) in `filter` callbacks.
 */
export function isActiveResolvedActivity(
  activity: ResolvedActivity,
): activity is ActiveResolvedActivity {
  return !isActivitySkippedOrCancelled(activity);
}
